// Durations, as minutes. Storage is minutes everywhere — time entries and task
// estimates alike — and this is the one place that decides how they are typed
// and spoken. Pure so it can be tested without a DOM; ui.tsx re-exports it.

/** A working day and week, as YouTrack counts them. An estimate imported as
 *  "1d" means a day of work, not twenty-four hours, and someone typing "1w"
 *  into an estimate means the same. */
const DAY = 8 * 60
const WEEK = 5 * DAY

/** Minutes to the "1h 30m" a timesheet is read in. Hours only, never days:
 *  "2h / 3h" next to an estimate has to compare at a glance, and a day is a
 *  convention a reader would have to know. */
export function formatDuration(minutes: number) {
  if (!minutes) return "0h"
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (!hours) return `${rest}m`
  if (!rest) return `${hours}h`
  return `${hours}h ${rest}m`
}

/** "1.5", "2h 30m", "90m", "1d", "1w 2d" — what someone actually types into a
 *  duration box. Returns null when it cannot be read, so the caller can say so
 *  rather than saving a silent zero. */
export function parseDuration(input: string): number | null {
  const text = input.trim().toLowerCase()
  if (!text) return null

  const composite =
    /^(?:(\d+(?:[.,]\d+)?)\s*w)?\s*(?:(\d+(?:[.,]\d+)?)\s*d)?\s*(?:(\d+(?:[.,]\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/.exec(text)
  if (composite && (composite[1] || composite[2] || composite[3] || composite[4])) {
    const num = (v: string | undefined) => (v ? Number(v.replace(",", ".")) : 0)
    const total = Math.round(
      num(composite[1]) * WEEK + num(composite[2]) * DAY + num(composite[3]) * 60 + num(composite[4]),
    )
    return total > 0 ? total : null
  }

  // A bare number is hours, because "log 2" means two hours to everyone who has
  // ever filled in a timesheet.
  const bare = Number(text.replace(",", "."))
  if (!Number.isFinite(bare) || bare <= 0) return null
  return Math.round(bare * 60)
}

/** "2h / 3h": time logged against the estimate, in that order. */
export function formatSpentOfEstimate(spent: number, estimate: number) {
  return `${formatDuration(spent)} / ${formatDuration(estimate)}`
}

/** The tooltip for the same pair, spelled out. */
export function describeSpentOfEstimate(spent: number, estimate: number) {
  return `Logged ${formatDuration(spent)} of ${formatDuration(estimate)} estimated`
}
